import { mutationOptions, type QueryClient } from '@tanstack/react-query'
import type { FetchWorkShiftsParams } from '../queries/hr.queries'
import { keys } from '../queries/keys'
import {
  clockIn,
  clockOut,
  copyPlanningWeek,
  createCollaborator,
  createCollaboratorClothing,
  createCollaboratorEquipment,
  createCollaboratorUnavailability,
  createWorkShift,
  deleteCollaborator,
  deleteCollaboratorClothing,
  deleteCollaboratorEquipment,
  deleteCollaboratorUnavailability,
  deleteWorkShift,
  endCollaboratorFormation,
  exportCollaboratorUnavailabilitiesToXlsx,
  exportPlanningWorkShiftsToXlsx,
  exportWorkShiftClocksToXlsx,
  optimizePlanning,
  publishPlanningSms,
  replaceCollaboratorWorkTimes,
  startCollaboratorFormation,
  updateCollaborator,
  updateCollaboratorClothing,
  updateCollaboratorEquipment,
  updateCollaboratorUnavailability,
  updatePlanningConfig,
  unvalidateWorkShift,
  updateWorkShift,
  updateWorkShiftValidatedTimes,
  validateWorkShift,
} from './hr.mutations'

const invalidateShiftDependentQueries = (client: QueryClient) => {
  client.invalidateQueries({ queryKey: keys.hr.workShifts.all() })
  client.invalidateQueries({ queryKey: [...keys.hr.all, 'store-weekly-metrics'] })
  client.invalidateQueries({ queryKey: [...keys.hr.all, 'remote-store-period-metrics'] })
  client.invalidateQueries({ queryKey: [...keys.hr.all, 'analytics'] })
  client.invalidateQueries({ queryKey: ['get-network-dashboard-kpis'] })
}

// ============================================================================
// Collaborators mutation options
// ============================================================================

export const createCollaboratorOptions = () =>
  mutationOptions({
    mutationFn: createCollaborator,
    onSuccess: (_data, _variables, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.collaborators.all() })
    },
  })

export const updateCollaboratorOptions = () =>
  mutationOptions({
    mutationFn: updateCollaborator,
    onSuccess: (_data, _variables, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.collaborators.all() })
    },
  })

export const deleteCollaboratorOptions = () =>
  mutationOptions({
    mutationFn: deleteCollaborator,
    onSuccess: (_data, _variables, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.collaborators.all() })
    },
  })

// ============================================================================
// Collaborator formation lifecycle mutation options (manager actions)
// ============================================================================

// Le toggle formation modifie la boutique d'imputation des shifts du
// collaborateur, donc on invalide aussi tous les KPIs agrégés par store.
export const startCollaboratorFormationOptions = () =>
  mutationOptions({
    mutationFn: startCollaboratorFormation,
    onSuccess: (_data, collaboratorId, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.collaborators.all() })
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(collaboratorId),
      })
      invalidateShiftDependentQueries(context.client)
    },
  })

export const endCollaboratorFormationOptions = () =>
  mutationOptions({
    mutationFn: endCollaboratorFormation,
    onSuccess: (_data, collaboratorId, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.collaborators.all() })
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(collaboratorId),
      })
      invalidateShiftDependentQueries(context.client)
    },
  })

// ============================================================================
// Collaborator work times mutation options (batch operations)
// ============================================================================

export const replaceCollaboratorWorkTimesOptions = () =>
  mutationOptions({
    mutationFn: replaceCollaboratorWorkTimes,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.workTimes(variables.collaboratorId),
      })
      // Also invalidate the collaborator since work times are nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

// ============================================================================
// Collaborator unavailabilities mutation options
// ============================================================================

export const createCollaboratorUnavailabilityOptions = () =>
  mutationOptions({
    mutationFn: createCollaboratorUnavailability,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.unavailabilities(variables.collaboratorId),
      })
      // Also invalidate the collaborator since unavailabilities are nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const updateCollaboratorUnavailabilityOptions = () =>
  mutationOptions({
    mutationFn: updateCollaboratorUnavailability,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.unavailabilities(variables.collaboratorId),
      })
      // Also invalidate the collaborator since unavailabilities are nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const deleteCollaboratorUnavailabilityOptions = () =>
  mutationOptions({
    mutationFn: deleteCollaboratorUnavailability,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.unavailabilities(variables.collaboratorId),
      })
      context.client.invalidateQueries({
        queryKey: keys.hr.absenceCalendar.all(),
      })
      // Also invalidate the collaborator since unavailabilities are nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

// ============================================================================
// Collaborator equipment mutation options
// ============================================================================

export const createCollaboratorEquipmentOptions = () =>
  mutationOptions({
    mutationFn: createCollaboratorEquipment,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.equipment(variables.collaboratorId),
      })
      // Also invalidate the collaborator since equipment is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const updateCollaboratorEquipmentOptions = () =>
  mutationOptions({
    mutationFn: updateCollaboratorEquipment,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.equipment(variables.collaboratorId),
      })
      // Also invalidate the collaborator since equipment is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const deleteCollaboratorEquipmentOptions = () =>
  mutationOptions({
    mutationFn: deleteCollaboratorEquipment,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.equipment(variables.collaboratorId),
      })
      // Also invalidate the collaborator since equipment is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

// ============================================================================
// Collaborator clothing mutation options
// ============================================================================

export const createCollaboratorClothingOptions = () =>
  mutationOptions({
    mutationFn: createCollaboratorClothing,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.clothing(variables.collaboratorId),
      })
      // Also invalidate the collaborator since clothing is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const updateCollaboratorClothingOptions = () =>
  mutationOptions({
    mutationFn: updateCollaboratorClothing,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.clothing(variables.collaboratorId),
      })
      // Also invalidate the collaborator since clothing is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

export const deleteCollaboratorClothingOptions = () =>
  mutationOptions({
    mutationFn: deleteCollaboratorClothing,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.clothing(variables.collaboratorId),
      })
      // Also invalidate the collaborator since clothing is nested
      context.client.invalidateQueries({
        queryKey: keys.hr.collaborators.byId(variables.collaboratorId),
      })
    },
  })

// ============================================================================
// Work shifts mutation options
// ============================================================================

export const createWorkShiftOptions = () =>
  mutationOptions({
    mutationFn: createWorkShift,
    onSuccess: (_data, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

export const updateWorkShiftOptions = () =>
  mutationOptions({
    mutationFn: updateWorkShift,
    onSettled: (_data, _error, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

export const updateWorkShiftValidatedTimesOptions = () =>
  mutationOptions({
    mutationFn: updateWorkShiftValidatedTimes,
    onSettled: (_data, _error, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

export const unvalidateWorkShiftOptions = () =>
  mutationOptions({
    mutationFn: unvalidateWorkShift,
    onSettled: (_data, _error, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

export const validateWorkShiftOptions = () =>
  mutationOptions({
    mutationFn: validateWorkShift,
    onSettled: (_data, _error, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

export const deleteWorkShiftOptions = () =>
  mutationOptions({
    mutationFn: deleteWorkShift,
    onSuccess: (_data, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

// ============================================================================
// Attendance mutation options
// ============================================================================

export const clockInOptions = () =>
  mutationOptions({
    mutationFn: clockIn,
    onSuccess: (_data, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
      context.client.invalidateQueries({ queryKey: keys.hr.attendanceHistory.all() })
    },
  })

export const clockOutOptions = () =>
  mutationOptions({
    mutationFn: clockOut,
    onSuccess: (_data, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
      context.client.invalidateQueries({ queryKey: keys.hr.attendanceHistory.all() })
    },
  })

export const exportWorkShiftClocksToXlsxOptions = () =>
  mutationOptions({
    mutationFn: exportWorkShiftClocksToXlsx,
  })

export const exportCollaboratorUnavailabilitiesToXlsxOptions = () =>
  mutationOptions({
    mutationFn: exportCollaboratorUnavailabilitiesToXlsx,
  })

export const exportPlanningWorkShiftsToXlsxOptions = () =>
  mutationOptions({
    mutationFn: exportPlanningWorkShiftsToXlsx,
  })

// ============================================================================
// Planning config mutation options
// ============================================================================

export const updatePlanningConfigOptions = () =>
  mutationOptions({
    mutationFn: updatePlanningConfig,
    onSuccess: (_data, _variables, _onResult, context) => {
      context.client.invalidateQueries({ queryKey: keys.hr.planningConfig.all() })
      // Rate multipliers (night/weekend) drive every cost-bearing analytics view —
      // bump them and the whole HR dashboard / network KPIs must refetch.
      invalidateShiftDependentQueries(context.client)
    },
  })

// ============================================================================
// Planning mutation options
// ============================================================================

export const optimizePlanningOptions = () =>
  mutationOptions({
    mutationFn: optimizePlanning,
    onSuccess: (_data, _variables, _onResult, context) => {
      invalidateShiftDependentQueries(context.client)
    },
  })

const shiftIsoDate = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export const copyPlanningWeekOptions = () =>
  mutationOptions({
    mutationFn: copyPlanningWeek,
    onSuccess: (data, _variables, _onResult, context) => {
      // Invalidation fine de la liste des shifts via predicate (logique
      // staging : on cible la période impactée et le store).
      const affectedStart = shiftIsoDate(data.targetPeriod.dateStart, -1)
      const affectedEnd = shiftIsoDate(data.targetPeriod.dateEnd, 1)

      context.client.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as readonly unknown[]
          if (queryKey[0] !== 'hr' || queryKey[1] !== 'work-shifts' || queryKey[2] !== 'list') {
            return false
          }
          const params = queryKey[3] as FetchWorkShiftsParams | undefined
          if (!params) return true

          if (params.storeId !== undefined && params.storeId !== data.storeId) {
            return false
          }

          const queryStart = params.dateStart ?? params.date
          const queryEnd = params.dateEnd ?? params.date
          if (!queryStart || !queryEnd) return true

          return queryStart <= affectedEnd && queryEnd >= affectedStart
        },
      })

      // KPIs agrégés (formation : on a refacto pour invalider tous les
      // KPIs dépendants des shifts). On invalide globalement car c'est
      // peu coûteux et plus simple que de filtrer par période.
      context.client.invalidateQueries({ queryKey: [...keys.hr.all, 'store-weekly-metrics'] })
      context.client.invalidateQueries({
        queryKey: [...keys.hr.all, 'remote-store-period-metrics'],
      })
      context.client.invalidateQueries({ queryKey: [...keys.hr.all, 'analytics'] })
      context.client.invalidateQueries({ queryKey: ['get-network-dashboard-kpis'] })
    },
  })

export const publishPlanningSmsOptions = () =>
  mutationOptions({
    mutationFn: publishPlanningSms,
  })
